import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnalysisAPI, OdkAPI } from "../api/endpoints";
import ProjectFormPicker from "../components/ProjectFormPicker";
import { Card, PageHeader, Spinner, ErrorBanner, HelpBox, KindBadge } from "../components/Layout";
import PlotlyChart from "../components/Plot";
import { varKind } from "../lib/varkind";
import type { Data } from "plotly.js-dist-min";

interface CrosstabResult {
  // pandas to_dict(): { categoría_columna: { categoría_fila: n } }
  counts: Record<string, Record<string, number>>;
  percentages: Record<string, Record<string, number>>;
  margins_of_error: Record<string, Record<string, number>>;
  n_total: number;
  chi2: { statistic: number | null; p_value: number | null; dof: number | null; interpretation: string };
  type?: string;
}

// Modos de tabla, con su explicación en pantalla.
type TableMode = "row" | "col" | "total" | "counts";
const TABLE_MODES: { key: TableMode; label: string; desc: string }[] = [
  {
    key: "row", label: "% por fila",
    desc: "Cada fila suma 100%. Responde: dentro de cada categoría de la variable de filas, ¿cómo se reparte la de columnas? Ej.: de los hombres, ¿qué % aprueba?",
  },
  {
    key: "col", label: "% por columna",
    desc: "Cada columna suma 100%. Responde: dentro de cada categoría de columnas, ¿cómo se reparte la de filas? Ej.: de los que aprueban, ¿qué % son hombres?",
  },
  {
    key: "total", label: "% del total",
    desc: "Cada celda es el % sobre el total de encuestados (toda la tabla suma 100%). Útil para ver el peso de cada combinación en la muestra completa.",
  },
  {
    key: "counts", label: "Frecuencias (n)",
    desc: "Conteos absolutos por celda, sin porcentajes. Útil para verificar tamaños de base antes de interpretar porcentajes.",
  },
];

type ChartMode = "group" | "stack" | "stack100" | "heatmap";
const CHART_MODES: { key: ChartMode; label: string; desc: string }[] = [
  { key: "group", label: "Barras agrupadas", desc: "Una barra por categoría de columnas, lado a lado dentro de cada fila. Ideal para comparar magnitudes." },
  { key: "stack", label: "Barras apiladas", desc: "Las categorías de columnas se apilan en una sola barra por fila. Muestra composición y total a la vez." },
  { key: "stack100", label: "Apiladas 100%", desc: "Cada barra se normaliza a 100%: compara la composición relativa entre filas aunque tengan bases distintas." },
  { key: "heatmap", label: "Mapa de calor", desc: "Matriz coloreada: más intenso = mayor valor. Ideal para tablas grandes donde las barras se saturan." },
];

export default function CrosstabsPage() {
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [varIndex, setVarIndex] = useState<string>("");
  const [varCol, setVarCol] = useState<string>("");
  const [tableMode, setTableMode] = useState<TableMode>("row");
  const [chartMode, setChartMode] = useState<ChartMode>("group");
  const [result, setResult] = useState<CrosstabResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const colsQ = useQuery({
    queryKey: ["dict", projectId, formId],
    queryFn: () => OdkAPI.variables(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });
  const cols = (colsQ.data ?? []).filter((v) => !v.is_technical);

  const run = async () => {
    if (!projectId || !formId || !varIndex || !varCol) return;
    setBusy(true); setErr(null);
    try {
      const r = await AnalysisAPI.descriptives({
        project_id: projectId, form_id: formId, variables: [varIndex], cross_var: varCol,
      });
      setResult((r as Record<string, CrosstabResult>)[varIndex] ?? null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error");
    } finally { setBusy(false); }
  };

  // ── Matriz base: filas = categorías de varIndex, columnas = de varCol ──
  // El backend serializa pandas como {columna: {fila: n}}; aquí lo reordenamos.
  const colCats = result ? Object.keys(result.counts) : [];
  const rowCats = result
    ? Array.from(new Set(colCats.flatMap((c) => Object.keys(result.counts[c] ?? {}))))
    : [];
  const n = (r: string, c: string) => result?.counts[c]?.[r] ?? 0;
  const rowTotal = (r: string) => colCats.reduce((s, c) => s + n(r, c), 0);
  const colTotal = (c: string) => rowCats.reduce((s, r) => s + n(r, c), 0);
  const grand = rowCats.reduce((s, r) => s + rowTotal(r), 0);

  // Valor de celda + MoE según el modo elegido (MoE al 95%: 1.96·√(p(1−p)/base)).
  const cell = (r: string, c: string): { value: number; moe: number | null } => {
    const count = n(r, c);
    if (tableMode === "counts") return { value: count, moe: null };
    const base = tableMode === "row" ? rowTotal(r) : tableMode === "col" ? colTotal(c) : grand;
    if (base <= 1) return { value: 0, moe: 0 };
    const p = count / base;
    return { value: p * 100, moe: 1.96 * Math.sqrt((p * (1 - p)) / base) * 100 };
  };

  // V de Cramér: fuerza de la asociación (0 = nula, 1 = perfecta).
  const cramersV = (() => {
    const chi2 = result?.chi2?.statistic;
    if (chi2 == null || grand === 0 || rowCats.length < 2 || colCats.length < 2) return null;
    const k = Math.min(rowCats.length, colCats.length) - 1;
    return Math.sqrt(chi2 / (grand * k));
  })();

  // ── Gráfico ──
  const chartValues = (r: string, c: string) => cell(r, c).value;
  const traces: Data[] =
    chartMode === "heatmap"
      ? [{
          type: "heatmap",
          x: colCats, y: rowCats,
          z: rowCats.map((r) => colCats.map((c) => chartValues(r, c))),
          colorscale: "Blues",
          hoverongaps: false,
        } as Data]
      : colCats.map((c) => ({
          type: "bar",
          name: c,
          x: rowCats,
          y: rowCats.map((r) => {
            if (chartMode === "stack100") {
              const t = rowTotal(r);
              return t > 0 ? (n(r, c) / t) * 100 : 0;
            }
            return chartValues(r, c);
          }),
        }));
  const barmode = chartMode === "group" ? "group" : chartMode === "heatmap" ? undefined : "stack";
  const unitLabel = tableMode === "counts" ? "n" : "%";

  const modeDoc = TABLE_MODES.find((m) => m.key === tableMode)!;
  const chartDoc = CHART_MODES.find((m) => m.key === chartMode)!;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader title="Tablas cruzadas (Crosstabs)" subtitle="Cruce de dos variables con totales, Chi², V de Cramér y varias vistas." />

      <HelpBox title="Guía: cómo leer una tabla cruzada y sus opciones">
        <p>
          Una tabla cruzada cuenta cuántos casos caen en cada combinación de dos variables categóricas
          (filas × columnas) y permite ver si están relacionadas.
        </p>
        <div>
          <b>Vistas de la tabla:</b>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            {TABLE_MODES.map((m) => <li key={m.key}><b>{m.label}.</b> {m.desc}</li>)}
          </ul>
        </div>
        <div>
          <b>Tipos de gráfico:</b>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            {CHART_MODES.map((m) => <li key={m.key}><b>{m.label}.</b> {m.desc}</li>)}
          </ul>
        </div>
        <p>
          <b>Medidas de asociación.</b> <b>Chi²</b> prueba si las dos variables son independientes:
          p &lt; 0.05 indica que existe relación estadísticamente significativa. <b>V de Cramér</b> mide la
          <i> fuerza</i> de esa relación en una escala 0–1 (≈0.1 débil, ≈0.3 moderada, ≥0.5 fuerte) —
          es la "correlación" apropiada para variables categóricas. <b>±MoE</b> es el margen de error al 95%
          de cada porcentaje, calculado sobre la base de su modo (fila, columna o total).
        </p>
        <p>
          <b>Totales.</b> La tabla incluye siempre totales por fila y por columna (base n), para que sepas
          sobre cuántos casos se calcula cada porcentaje. Desconfía de porcentajes con base pequeña (n &lt; 30).
        </p>
      </HelpBox>

      <Card className="mb-4">
        <ProjectFormPicker projectId={projectId} formId={formId}
          onProject={(id) => { setProjectId(id); setFormId(null); }} onForm={setFormId} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="etiqueta">Variable (filas)</label>
            <select className="input" value={varIndex} onChange={(e) => setVarIndex(e.target.value)}>
              <option value="">—</option>
              {cols.map((c) => <option key={c.name} value={c.name}>{c.label || c.name}</option>)}
            </select>
            {varIndex && cols.find((c) => c.name === varIndex) && (
              <div className="mt-1"><KindBadge kind={varKind(cols.find((c) => c.name === varIndex)!)} /></div>
            )}
          </div>
          <div>
            <label className="etiqueta">Variable (columnas)</label>
            <select className="input" value={varCol} onChange={(e) => setVarCol(e.target.value)}>
              <option value="">—</option>
              {cols.map((c) => <option key={c.name} value={c.name}>{c.label || c.name}</option>)}
            </select>
            {varCol && cols.find((c) => c.name === varCol) && (
              <div className="mt-1"><KindBadge kind={varKind(cols.find((c) => c.name === varCol)!)} /></div>
            )}
          </div>
        </div>
        <button onClick={run} disabled={busy || !varIndex || !varCol}
          className="btn mt-3">
          {busy ? <Spinner /> : "Calcular"}
        </button>
      </Card>

      {err && <ErrorBanner message={err} />}

      {result && (
        <div className="space-y-4">
          <Card>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-tinta-2 mb-3">
              <span>n = {result.n_total}</span>
              {result.chi2.statistic != null && (
                <span>
                  Chi² = {result.chi2.statistic?.toFixed(3)} · gl = {result.chi2.dof} · p = {result.chi2.p_value?.toFixed(4)} —
                  <span className={result.chi2.p_value! < 0.05 ? "text-estado-ok" : "text-tinta-3"}>
                    {" "}{result.chi2.interpretation}
                  </span>
                </span>
              )}
              {cramersV != null && (
                <span title="Fuerza de la asociación entre las dos variables (0 = nula, 1 = perfecta)">
                  V de Cramér = <b className="text-marca">{cramersV.toFixed(3)}</b>
                  <span className="text-tinta-3">
                    {" "}({cramersV < 0.1 ? "muy débil" : cramersV < 0.3 ? "débil" : cramersV < 0.5 ? "moderada" : "fuerte"})
                  </span>
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-4 mb-2">
              <div>
                <label className="etiqueta">Vista de la tabla</label>
                <div className="flex gap-1">
                  {TABLE_MODES.map((m) => (
                    <button key={m.key} onClick={() => setTableMode(m.key)}
                      className={`px-2.5 py-1 rounded-lg text-xs ${tableMode === m.key ? "bg-marca text-tinta" : "bg-plano text-tinta-2 border border-grid hover:bg-grid"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="etiqueta">Tipo de gráfico</label>
                <div className="flex gap-1">
                  {CHART_MODES.map((m) => (
                    <button key={m.key} onClick={() => setChartMode(m.key)}
                      className={`px-2.5 py-1 rounded-lg text-xs ${chartMode === m.key ? "bg-marca text-tinta" : "bg-plano text-tinta-2 border border-grid hover:bg-grid"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-tinta-3 mb-2">{modeDoc.desc} — Gráfico: {chartDoc.desc}</p>

            <div className="h-80">
              <PlotlyChart
                data={traces}
                layout={{
                  ...(barmode ? { barmode } : {}),
                  yaxis: { title: chartMode === "stack100" ? "% (composición por fila)" : unitLabel },
                }}
              />
            </div>
          </Card>

          <Card>
            <div className="text-sm font-medium text-tinta-2 mb-2">
              Tabla — {modeDoc.label}{tableMode !== "counts" && " (±MoE 95%)"}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-tinta-3 border-b border-grid">
                  <tr>
                    <th className="py-2 pr-3 text-left">{varIndex} \ {varCol}</th>
                    {colCats.map((c) => <th key={c} className="py-2 pr-3 text-right">{c}</th>)}
                    <th className="py-2 pr-3 text-right text-tinta-2">Total (n)</th>
                  </tr>
                </thead>
                <tbody>
                  {rowCats.map((r) => (
                    <tr key={r} className="border-b border-grid">
                      <td className="py-2 pr-3 font-mono text-marca">{r}</td>
                      {colCats.map((c) => {
                        const { value, moe } = cell(r, c);
                        return (
                          <td key={c} className="py-2 pr-3 text-right">
                            {tableMode === "counts" ? value : `${value.toFixed(1)}%`}
                            {moe != null && tableMode !== "counts" && (
                              <span className="text-tinta-3 text-xs"> ±{moe.toFixed(1)}</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="py-2 pr-3 text-right text-tinta-2">{rowTotal(r)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-grid">
                    <td className="py-2 pr-3 text-tinta-2 font-medium">Total (n)</td>
                    {colCats.map((c) => (
                      <td key={c} className="py-2 pr-3 text-right text-tinta-2">{colTotal(c)}</td>
                    ))}
                    <td className="py-2 pr-3 text-right text-tinta font-medium">{grand}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
